<script setup>
/* global axios */
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import ConversationApi from 'dashboard/api/conversations';
import InboxConversationApi from 'dashboard/api/inbox/conversation';
import { emitter } from 'shared/helpers/mitt';

const store = useStore();
const route = useRoute();
const router = useRouter();

const labels = useMapGetter('labels/getLabels');
const kanbanConversations = ref([]);
const pipelines = computed(() => store.getters['pipelines/getPipelines'] || []);
const agents = useMapGetter('agents/getAgents');
const inboxes = useMapGetter('inboxes/getInboxes');

const searchQuery = ref('');
const toastMsg = ref('');
const savedPipelineId = (() => { try { const v = localStorage.getItem('kb_pipeline'); return v ? parseInt(v, 10) : null; } catch { return null; } })();
const selectedPipelineId = ref(savedPipelineId);
const loading = ref(true);
const loadingConvs = ref(false);
const showFilters = ref(false);
const searchFocused = ref(false);
const filterAgent = ref('');
const filterInbox = ref('');
const filterUnread = ref(false);
let toastTimer = null;
let pollTimer = null;
let wsDebounce = null;
const POLL_INTERVAL = 30000;
const WS_DEBOUNCE_MS = 800;

const hasActiveFilters = computed(() => !!filterAgent.value || !!filterInbox.value || filterUnread.value);
const activeFilterCount = computed(() => [filterAgent.value, filterInbox.value, filterUnread.value].filter(Boolean).length);

const selectedPipeline = computed(() => {
  if (!pipelines.value.length) return null;
  if (selectedPipelineId.value) {
    return pipelines.value.find(p => p.id === selectedPipelineId.value) || pipelines.value[0];
  }
  return pipelines.value[0];
});

const hasPipelines = computed(() => pipelines.value.length > 0);

const labelMap = computed(() => {
  const m = {};
  (labels.value || []).forEach(l => { m[l.title] = l; });
  return m;
});

function isGroup(c) {
  const id = c.meta?.sender?.identifier || '';
  const ph = c.meta?.sender?.phone_number || '';
  return id.includes('@g.us') || ph.includes('@g.us');
}

function clearFilters() {
  filterAgent.value = '';
  filterInbox.value = '';
  filterUnread.value = false;
}

async function loadPipelineConversations(pipelineId, silent = false) {
  if (!pipelineId) return;
  if (!silent) loadingConvs.value = true;
  if (silent && dCard) return;
  try {
    const accountId = route.params.accountId;
    const { data } = await axios.get(`/api/v1/accounts/${accountId}/pipelines/${pipelineId}/conversations`);
    if (!dCard) {
      kanbanConversations.value = (data || []).filter(c => !isGroup(c));
    }
  } catch (e) {
    if (!silent) console.error('Kanban load error:', e);
    if (!silent) kanbanConversations.value = [];
  }
  if (!silent) loadingConvs.value = false;
}

function refreshNow() {
  clearTimeout(wsDebounce);
  wsDebounce = setTimeout(() => {
    const p = selectedPipeline.value;
    if (p) loadPipelineConversations(p.id, true);
  }, WS_DEBOUNCE_MS);
}

function startPolling() {
  stopPolling();
  pollTimer = setInterval(() => {
    const p = selectedPipeline.value;
    if (p) loadPipelineConversations(p.id, true);
  }, POLL_INTERVAL);
  emitter.on('fetch_conversation_stats', refreshNow);
}

function stopPolling() {
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
  clearTimeout(wsDebounce);
  emitter.off('fetch_conversation_stats', refreshNow);
}

const columns = computed(() => {
  if (!selectedPipeline.value) return [];
  const stages = selectedPipeline.value.pipeline_stages || [];
  const lc = searchQuery.value.toLowerCase();

  const matchFn = c => {
    if (lc) {
      const name = c.meta?.sender?.name || '';
      const msg = (c.messages && c.messages[0]?.content) || '';
      const phone = c.meta?.sender?.phone_number || '';
      if (!name.toLowerCase().includes(lc) && !msg.toLowerCase().includes(lc) && !phone.includes(lc)) return false;
    }
    if (filterAgent.value) {
      const assigneeId = c.meta?.assignee?.id;
      if (filterAgent.value === '__unassigned') { if (assigneeId) return false; }
      else if (String(assigneeId) !== String(filterAgent.value)) return false;
    }
    if (filterInbox.value && String(c.inbox_id) !== String(filterInbox.value)) return false;
    if (filterUnread.value && !c.unread_count) return false;
    return true;
  };

  return stages.map(stage => ({
    id: stage.id,
    stageId: stage.id,
    title: stage.name,
    color: stage.color || '#94a3b8',
    conversations: kanbanConversations.value.filter(c => c.pipeline_stage_id === stage.id).filter(matchFn),
  }));
});

const totalActive = computed(() => kanbanConversations.value.length);

function lastMsgText(c) {
  return (c.messages && c.messages[0]?.content) || '';
}

function contactName(c) { return c.meta?.sender?.name || `#${c.id}`; }

function agentInitials(c) {
  const a = c.meta?.assignee;
  if (!a?.name) return '';
  const p = a.name.split(' ').filter(Boolean);
  return p.length >= 2 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : a.name.substring(0, 2).toUpperCase();
}

function agentColor(c) {
  const name = c.meta?.assignee?.name || '';
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return `hsl(${Math.abs(h) % 360}, 42%, 48%)`;
}

function timeAgo(c) {
  const ts = c.timestamp || c.last_activity_at || c.created_at;
  if (!ts) return '';
  const diff = Math.floor((Date.now() / 1000 - ts) / 60);
  if (diff < 1) return 'agora';
  if (diff < 60) return diff + 'min';
  if (diff < 1440) return Math.floor(diff / 60) + 'h';
  return Math.floor(diff / 1440) + 'd';
}

function isWhatsapp(c) {
  const ch = c.meta?.channel || '';
  return ch.includes('Whatsapp') || ch.includes('Api');
}

function flash(msg) {
  clearTimeout(toastTimer);
  toastMsg.value = msg;
  toastTimer = setTimeout(() => { toastMsg.value = ''; }, 2200);
}

function openConversation(c) {
  if (didDrag) { didDrag = false; return; }
  router.push({
    name: 'inbox_conversation',
    params: { accountId: route.params.accountId, conversation_id: c.id },
    query: { from: 'kanban' },
  });
}

async function moveToStage(convId, toStageId) {
  const c = kanbanConversations.value.find(x => x.id === convId);
  if (!c) return;
  const stageId = parseInt(toStageId, 10);
  const col = columns.value.find(col => col.stageId === stageId);
  const colTitle = col?.title || '';

  c.pipeline_stage_id = stageId;
  flash(`${contactName(c)} → ${colTitle}`);
  InboxConversationApi.update(convId, { pipeline_stage_id: stageId }).catch(() => {
    flash('Erro ao mover');
  });
}

async function handleDrop(convId, fromColId, toColId) {
  await moveToStage(convId, toColId);
}

// ── Drag & Drop (vanilla JS) ──
let dCard = null, ghost = null, ph = null, ox = 0, oy = 0, scrollRAF = null, dragFromCol = '';
let pendingDrag = null, dragStarted = false, didDrag = false;
const DRAG_THRESHOLD = 5;

function bindDrag() {
  const el = document.getElementById('kb-board');
  if (!el) return;
  el.querySelectorAll('.card').forEach(c => {
    c.addEventListener('mousedown', eMD);
    c.addEventListener('touchstart', eTS, { passive: false });
  });
}

function eMD(e) {
  if (e.button !== 0 || e.target.closest('button,input')) return;
  dragStarted = false;
  pendingDrag = { el: e.currentTarget, sx: e.clientX, sy: e.clientY };
  dragFromCol = e.currentTarget.closest('.col__cards')?.dataset.col || '';
  document.addEventListener('mousemove', eMM);
  document.addEventListener('mouseup', eMU);
}
function eTS(e) {
  if (e.target.closest('button,input')) return;
  e.preventDefault();
  const t = e.touches[0];
  dragStarted = false;
  pendingDrag = { el: e.currentTarget, sx: t.clientX, sy: t.clientY };
  dragFromCol = e.currentTarget.closest('.col__cards')?.dataset.col || '';
  document.addEventListener('touchmove', eTM, { passive: false });
  document.addEventListener('touchend', eTE);
}

function startD(el, x, y) {
  dCard = el; const rect = el.getBoundingClientRect(); ox = x - rect.left; oy = y - rect.top;
  ghost = el.cloneNode(true); ghost.className = 'card drag-ghost'; ghost.style.width = rect.width + 'px';
  ghost.style.left = (x - ox) + 'px'; ghost.style.top = (y - oy) + 'px';
  document.body.appendChild(ghost); el.classList.add('dragging');
  ph = document.createElement('div'); ph.className = 'drop-placeholder'; ph.style.minHeight = rect.height + 'px';
  el.parentNode.insertBefore(ph, el);
}

function eMM(e) {
  if (!dragStarted && pendingDrag) {
    const dx = e.clientX - pendingDrag.sx, dy = e.clientY - pendingDrag.sy;
    if (Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD) return;
    startD(pendingDrag.el, pendingDrag.sx, pendingDrag.sy);
    dragStarted = true; didDrag = true; pendingDrag = null;
  }
  moveD(e.clientX, e.clientY);
}
function eTM(e) {
  e.preventDefault();
  const t = e.touches[0];
  if (!dragStarted && pendingDrag) {
    const dx = t.clientX - pendingDrag.sx, dy = t.clientY - pendingDrag.sy;
    if (Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD) return;
    startD(pendingDrag.el, pendingDrag.sx, pendingDrag.sy);
    dragStarted = true; didDrag = true; pendingDrag = null;
  }
  moveD(t.clientX, t.clientY);
}

function moveD(x, y) {
  if (!ghost) return;
  ghost.style.left = (x - ox) + 'px'; ghost.style.top = (y - oy) + 'px';
  const boardEl = document.getElementById('kb-board');
  if (boardEl) {
    const br = boardEl.getBoundingClientRect(); const edge = 60;
    cancelAnimationFrame(scrollRAF);
    if (x < br.left + edge && boardEl.scrollLeft > 0) { (function sL() { boardEl.scrollLeft -= 6; if (ghost && x < br.left + edge) scrollRAF = requestAnimationFrame(sL); })(); }
    else if (x > br.right - edge) { (function sR() { boardEl.scrollLeft += 6; if (ghost && x > br.right - edge) scrollRAF = requestAnimationFrame(sR); })(); }
  }
  const cols = document.querySelectorAll('.col__cards');
  cols.forEach(c => c.classList.remove('drag-over'));
  cols.forEach(col => {
    const cr = col.getBoundingClientRect();
    if (x >= cr.left && x <= cr.right) {
      col.classList.add('drag-over');
      const cards = [...col.querySelectorAll('.card:not(.dragging)')];
      let ib = null;
      for (const c of cards) { const ccr = c.getBoundingClientRect(); if (y < ccr.top + ccr.height / 2) { ib = c; break; } }
      if (ph) { if (ib) col.insertBefore(ph, ib); else col.appendChild(ph); }
    }
  });
}

function eMU(e) {
  document.removeEventListener('mousemove', eMM);
  document.removeEventListener('mouseup', eMU);
  if (!dragStarted) { pendingDrag = null; return; }
  endD(e.clientX, e.clientY);
}
function eTE(e) {
  document.removeEventListener('touchmove', eTM);
  document.removeEventListener('touchend', eTE);
  if (!dragStarted) { pendingDrag = null; return; }
  const t = e.changedTouches[0]; endD(t.clientX, t.clientY);
}

function updateHeaders() {
  document.querySelectorAll('.col[data-col]').forEach(col => {
    const cards = col.querySelectorAll('.col__cards .card:not(.dragging)');
    const n = col.querySelector('.col__count'); if (n) n.textContent = cards.length;
  });
}

function endD(x, y) {
  if (!dCard || !ghost) return;
  cancelAnimationFrame(scrollRAF);
  const cols = document.querySelectorAll('.col__cards'); cols.forEach(c => c.classList.remove('drag-over'));
  let tc = null; cols.forEach(col => { const cr = col.getBoundingClientRect(); if (x >= cr.left && x <= cr.right) tc = col; });
  const convId = parseInt(dCard.dataset.id, 10);
  const card = dCard; const g = ghost; const p = ph;
  const gr = g.getBoundingClientRect(); const pr = p ? p.getBoundingClientRect() : null;

  if (tc && pr) {
    const toCol = tc.dataset.col || '';
    const a = g.animate([
      { left: gr.left + 'px', top: gr.top + 'px', transform: 'rotate(1.4deg)', boxShadow: 'var(--shadow-lg)' },
      { left: pr.left + 'px', top: pr.top + 'px', transform: 'rotate(0) scale(1)', boxShadow: 'var(--shadow)' },
    ], { duration: 200, easing: 'cubic-bezier(.22,.68,.31,1)', fill: 'forwards' });
    a.onfinish = () => {
      g.remove(); card.classList.remove('dragging'); card.style.animation = 'none';
      if (p && p.parentNode) p.parentNode.insertBefore(card, p);
      if (p) p.remove(); updateHeaders();
      if (toCol !== dragFromCol) handleDrop(convId, dragFromCol, toCol);
    };
  } else {
    const or = card.getBoundingClientRect();
    if (or.width > 0) {
      const a = g.animate([
        { left: gr.left + 'px', top: gr.top + 'px', transform: 'rotate(1.4deg)' },
        { left: or.left + 'px', top: or.top + 'px', transform: 'rotate(0) scale(1)' },
      ], { duration: 200, easing: 'cubic-bezier(.22,.68,.31,1)', fill: 'forwards' });
      a.onfinish = () => { g.remove(); if (p) p.remove(); card.classList.remove('dragging'); };
    } else { g.remove(); if (p) p.remove(); card.classList.remove('dragging'); }
  }
  ghost = null; ph = null; dCard = null;
}

// ── Context menu ──
const ctxOpen = ref(false);
const ctxX = ref(0);
const ctxY = ref(0);
const ctxConv = ref(null);
const ctxView = ref('main');

function showCtx(e, c) {
  e.preventDefault(); ctxConv.value = c; ctxView.value = 'main';
  ctxX.value = Math.min(e.clientX, window.innerWidth - 228);
  ctxY.value = Math.min(e.clientY, window.innerHeight - 320);
  ctxOpen.value = true;
}
function closeCtx() { ctxOpen.value = false; ctxView.value = 'main'; }

function ctxAction(action) {
  const c = ctxConv.value; if (!c) return;
  if (action === 'open') openConversation(c);
  else if (action === 'copy') {
    const ph = c.meta?.sender?.phone_number || '';
    if (ph) { navigator.clipboard?.writeText(ph); flash('Telefone copiado'); }
    else flash('Sem telefone');
  }
  closeCtx();
}

async function ctxMoveTo(colId) {
  const c = ctxConv.value; if (!c) return;
  const col = columns.value.find(col => String(col.id) === String(colId));
  const colTitle = col?.title || '';
  closeCtx();

  const stageId = parseInt(colId, 10);
  c.pipeline_stage_id = stageId;
  flash(`${contactName(c)} → ${colTitle}`);
  InboxConversationApi.update(c.id, { pipeline_stage_id: stageId }).catch(() => flash('Erro ao mover'));
}

// ── Lifecycle ──
onMounted(async () => {
  await Promise.all([
    store.dispatch('pipelines/get'),
    store.dispatch('labels/get'),
    store.dispatch('agents/get'),
    store.dispatch('inboxes/get'),
  ]);

  if (selectedPipeline.value) {
    await loadPipelineConversations(selectedPipeline.value.id);
  }

  loading.value = false;
  startPolling();
  document.addEventListener('click', closeCtx);
  document.addEventListener('click', () => { showFilters.value = false; });
});

onBeforeUnmount(() => {
  stopPolling();
  document.removeEventListener('click', closeCtx);
});

watch(selectedPipelineId, async (newId) => {
  try { localStorage.setItem('kb_pipeline', String(newId)); } catch {}
  const p = pipelines.value.find(pp => pp.id === newId) || pipelines.value[0];
  if (p) await loadPipelineConversations(p.id);
});

watch(columns, () => { if (!loading.value) nextTick(() => bindDrag()); }, { deep: true });
watch(loading, (val) => { if (!val) nextTick(() => bindDrag()); });
</script>

<template>
  <div class="kb">
    <!-- Loading skeleton -->
    <div v-if="loading" class="kb-loading">
      <div class="kb-loading__header">
        <div class="sk sk--title" />
        <div class="sk sk--search" />
        <div class="sk sk--btn" />
      </div>
      <div class="kb-loading__tabs">
        <div class="sk sk--tab" />
        <div class="sk sk--tab" />
      </div>
      <div class="kb-loading__board">
        <div v-for="n in 5" :key="n" class="kb-loading__col">
          <div class="sk sk--col-head" />
          <div v-for="m in (n === 1 ? 3 : n === 2 ? 2 : 1)" :key="m" class="sk sk--card" />
        </div>
      </div>
    </div>

    <!-- Real content -->
    <template v-else>
    <div class="header">
      <div class="header__row">
        <div class="header__left">
          <h1 class="header__title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="18" rx="1.5"/><rect x="14" y="3" width="7" height="10" rx="1.5"/></svg>
            Kanban
          </h1>
          <div class="header__tabs">
            <button
              v-for="p in pipelines"
              :key="p.id"
              class="htab"
              :class="{ on: selectedPipeline?.id === p.id }"
              @click="selectedPipelineId = p.id"
            >
              {{ p.name }}
              <span class="htab__n">{{ selectedPipeline?.id === p.id ? totalActive : '' }}</span>
            </button>
            <div v-if="!pipelines.length" class="header__empty">
              Nenhum pipeline configurado
            </div>
          </div>
        </div>

        <div class="header__right">
          <button
            class="header__filter-btn"
            :class="{ active: showFilters || hasActiveFilters }"
            @click.stop="showFilters = !showFilters"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/></svg>
            Filtrar
            <span v-if="activeFilterCount" class="header__filter-badge">{{ activeFilterCount }}</span>
          </button>
          <div class="header__stat">
            <span class="header__stat-n">{{ totalActive }}</span>
            <span class="header__stat-label">ativas</span>
          </div>
        </div>
      </div>

      <!-- Filter panel -->
      <div v-if="showFilters" class="filters" @click.stop>
        <div class="filters__row">
          <div class="filters__search" :class="{ focused: searchFocused }">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><circle cx="10.5" cy="10.5" r="6.5"/><line x1="21" y1="21" x2="15.5" y2="15.5"/></svg>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Buscar por nome, telefone..."
              @focus="searchFocused = true"
              @blur="searchFocused = false"
            />
            <button v-if="searchQuery" class="filters__search-clear" @click="searchQuery = ''">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div class="filters__sep" />
          <div class="filters__field">
            <label>Agente</label>
            <select v-model="filterAgent">
              <option value="">Todos</option>
              <option value="__unassigned">Sem agente</option>
              <option v-for="a in agents" :key="a.id" :value="a.id">{{ a.name }}</option>
            </select>
          </div>
          <div class="filters__field">
            <label>Inbox</label>
            <select v-model="filterInbox">
              <option value="">Todas</option>
              <option v-for="ib in inboxes" :key="ib.id" :value="ib.id">{{ ib.name }}</option>
            </select>
          </div>
          <label class="filters__check">
            <input v-model="filterUnread" type="checkbox" />
            <span>Apenas não lidas</span>
          </label>
          <button v-if="hasActiveFilters || searchQuery" class="filters__clear" @click="clearFilters(); searchQuery = ''">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            Limpar tudo
          </button>
        </div>
      </div>
    </div>

    <div id="kb-board" class="board">
      <div
        v-for="col in columns"
        :key="col.id"
        class="col"
        :data-col="col.id"
      >
        <div class="col__head">
          <span class="col__dot" :style="{ background: col.color }" />
          <span class="col__title">{{ col.title }}</span>
          <span class="col__count">{{ col.conversations.length }}</span>
        </div>
        <div class="col__cards" :data-col="col.id">
          <div
            v-for="(c, ci) in col.conversations"
            :key="c.id"
            class="card"
            :data-id="c.id"
            :style="{ animationDelay: (ci * 30) + 'ms' }"
            @contextmenu="showCtx($event, c)"
            @click="openConversation(c)"
          >
            <div class="card__name">{{ contactName(c) }}</div>
            <div class="card__msg">{{ lastMsgText(c) || 'Sem mensagens' }}</div>
            <div class="card__footer">
              <span
                v-for="lbl in (c.labels || [])"
                :key="lbl"
                class="card__tag"
              >
                <span class="card__tag-dot" :style="{ background: labelMap[lbl]?.color || '#94a3b8' }" />
                {{ lbl }}
              </span>
              <div class="card__meta">
                <svg v-if="isWhatsapp(c)" width="13" height="13" viewBox="0 0 24 24" fill="currentColor" style="color:var(--t4);flex-shrink:0"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" style="color:var(--t4);flex-shrink:0"><circle cx="12" cy="12" r="9.5"/><path d="M2.5 12h19M12 2.5a15 15 0 0 1 0 19 15 15 0 0 1 0-19Z"/></svg>
                <span class="card__time">{{ timeAgo(c) }}</span>
                <span
                  v-if="c.meta?.assignee"
                  class="card__av"
                  :title="c.meta.assignee.name"
                  :style="{ background: agentColor(c), color: '#fff' }"
                >{{ agentInitials(c) }}</span>
              </div>
            </div>
            <div v-if="c.unread_count" class="card__unread">{{ c.unread_count }}</div>
          </div>
        </div>
      </div>
    </div>
    </template>

    <!-- Context menu -->
    <div
      v-if="ctxOpen"
      class="ctx open"
      :style="{ left: ctxX + 'px', top: ctxY + 'px' }"
      @click.stop
    >
      <template v-if="ctxView === 'main'">
        <button class="ctx__item" @click="ctxAction('open')"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>Abrir conversa</button>
        <button class="ctx__item" @click="ctxAction('copy')"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>Copiar telefone</button>
        <div class="ctx__sep" />
        <button class="ctx__item" @click="ctxView = 'move'"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>Mover para...<svg class="ctx__arrow" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg></button>
      </template>
      <template v-if="ctxView === 'move'">
        <button class="ctx__back" @click="ctxView = 'main'"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M15 18l-6-6 6-6"/></svg> Voltar</button>
        <div class="ctx__sub-title">Mover para</div>
        <button
          v-for="col in columns"
          :key="col.id"
          class="ctx__item"
          @click="ctxMoveTo(col.id)"
        ><span class="ctx__stage-dot" :style="{ background: col.color }" />{{ col.title }}</button>
      </template>
    </div>

    <!-- Toast -->
    <div v-if="toastMsg" class="toast on">{{ toastMsg }}</div>
  </div>
</template>

<style scoped>
.kb {
  --bg:#ffffff;--surface:#ffffff;--col:#f4f5f7;--hover:#f3f4f6;--active:#e9ebef;--field:#f6f7f9;
  --t1:#1f2937;--t2:#6b7280;--t3:#9ca3af;--t4:#c4c8ce;
  --line:#e8e9ec;--line-2:#dcdfe3;
  --brand:#1f6feb;--brand-soft:#eaf2fe;--brand-text:#1a5fcc;
  --ok:#15803d;--warn:#b45309;--bad:#be123c;
  --shadow:0 1px 2px rgba(16,24,40,.05);
  --shadow-lg:0 12px 28px rgba(16,24,40,.16),0 2px 6px rgba(16,24,40,.08);
  --card-pad:12px 13px;--card-gap:8px;--msg:block;
  --ff:'Inter',-apple-system,system-ui,sans-serif;
  font-family:var(--ff);font-size:14px;color:var(--t1);background:var(--bg);
  height:100%;display:flex;flex-direction:column;overflow:hidden;-webkit-font-smoothing:antialiased;line-height:1.45;
}


@keyframes riseIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}

.header{background:var(--surface);border-bottom:1px solid var(--line);flex-shrink:0}
.header__row{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:0 20px;height:56px}
.header__left{display:flex;align-items:center;gap:6px;min-width:0}
.header__right{display:flex;align-items:center;gap:8px;flex-shrink:0}

.header__title{display:flex;align-items:center;gap:8px;font-size:17px;font-weight:700;letter-spacing:-.025em;margin:0;white-space:nowrap;color:var(--t1)}
.header__title svg{color:var(--t3);flex-shrink:0}

.header__tabs{display:flex;align-items:center;gap:2px;margin-left:8px}
.htab{display:flex;align-items:center;gap:6px;padding:6px 12px;font-size:13px;font-weight:600;color:var(--t2);border-radius:8px;border:none;background:none;cursor:pointer;transition:all .12s;font-family:var(--ff);white-space:nowrap}
.htab:hover{background:var(--hover);color:var(--t1)}
.htab.on{background:var(--brand-soft);color:var(--brand-text)}
.htab__n{min-width:18px;height:18px;padding:0 5px;border-radius:5px;background:var(--active);color:var(--t3);font-size:11px;font-weight:700;display:flex;align-items:center;justify-content:center;font-variant-numeric:tabular-nums}
.htab.on .htab__n{background:rgba(31,111,235,.15);color:var(--brand-text)}


.header__filter-btn{display:flex;align-items:center;gap:6px;height:34px;padding:0 11px;border-radius:8px;border:1px solid var(--line);background:var(--surface);color:var(--t2);font-size:13px;font-weight:500;cursor:pointer;transition:all .1s;font-family:var(--ff);white-space:nowrap;position:relative}
.header__filter-btn:hover{background:var(--hover);color:var(--t1);border-color:var(--line-2)}
.header__filter-btn.active{border-color:var(--brand);color:var(--brand-text);background:var(--brand-soft)}
.header__filter-btn svg{flex-shrink:0}
.header__filter-badge{min-width:16px;height:16px;padding:0 4px;border-radius:8px;background:var(--brand);color:#fff;font-size:10px;font-weight:700;display:flex;align-items:center;justify-content:center}

.header__stat{display:flex;align-items:baseline;gap:4px;padding:0 4px 0 6px}
.header__stat-n{font-size:18px;font-weight:700;color:var(--t1);font-variant-numeric:tabular-nums;letter-spacing:-.02em}
.header__stat-label{font-size:12px;color:var(--t3);font-weight:500}

.filters{padding:0 20px 10px;animation:riseIn .12s ease-out}
.filters__row{display:flex;align-items:center;gap:10px;flex-wrap:wrap}
.filters__field{display:flex;align-items:center;gap:6px}
.filters__field label{font-size:12px;font-weight:600;color:var(--t3);text-transform:uppercase;letter-spacing:.03em;white-space:nowrap}
.filters__field select{height:32px;padding:0 28px 0 10px;border-radius:7px;border:1px solid var(--line);background:var(--field);font-size:13px;color:var(--t1);font-family:var(--ff);cursor:pointer;outline:none;appearance:none;background-image:url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%239ca3af' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 9px center;min-width:120px;transition:border-color .12s}
.filters__field select:focus{border-color:var(--brand)}
.filters__check{display:flex;align-items:center;gap:6px;font-size:13px;color:var(--t2);cursor:pointer;user-select:none;padding:0 4px}
.filters__check input{width:15px;height:15px;border-radius:4px;accent-color:var(--brand);cursor:pointer}
.filters__check span{white-space:nowrap}
.filters__search{display:flex;align-items:center;gap:7px;height:32px;padding:0 10px;border-radius:7px;border:1px solid var(--line);background:var(--field);transition:all .15s;min-width:200px;flex:1;max-width:280px}
.filters__search.focused{border-color:var(--brand);background:var(--surface);box-shadow:0 0 0 3px rgba(31,111,235,.1)}
.filters__search svg{flex-shrink:0;color:var(--t3)}
.filters__search input{flex:1;border:none;background:none;outline:none;font-size:13px;color:var(--t1);font-family:var(--ff);min-width:0}
.filters__search input::placeholder{color:var(--t4)}
.filters__search-clear{display:flex;align-items:center;justify-content:center;width:18px;height:18px;border:none;background:var(--active);border-radius:4px;cursor:pointer;padding:0;color:var(--t3);transition:all .1s}
.filters__search-clear:hover{background:var(--line-2);color:var(--t1)}
.filters__sep{width:1px;height:22px;background:var(--line);flex-shrink:0}
.filters__clear{display:flex;align-items:center;gap:5px;height:28px;padding:0 10px;border-radius:6px;border:none;background:var(--active);color:var(--t2);font-size:12px;font-weight:500;cursor:pointer;transition:all .1s;font-family:var(--ff);margin-left:4px}
.filters__clear:hover{background:var(--line-2);color:var(--t1)}
.header__fill{flex:1}

.board{display:flex;gap:10px;padding:12px 16px 14px;flex:1;overflow-x:auto;overflow-y:hidden;min-width:0}
.board::-webkit-scrollbar{height:8px}
.board::-webkit-scrollbar-track{background:transparent}
.board::-webkit-scrollbar-thumb{background:var(--line-2);border-radius:8px;border:2px solid transparent;background-clip:padding-box}

.col{flex:1 0 240px;min-width:240px;display:flex;flex-direction:column;background:var(--col);border-radius:12px;max-height:100%}
.col__head{display:flex;align-items:center;gap:8px;padding:13px 14px 10px;flex-shrink:0}
.col__dot{width:9px;height:9px;border-radius:3px;flex-shrink:0}
.col__title{font-size:14px;font-weight:600;letter-spacing:-.01em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.col__count{min-width:21px;height:21px;padding:0 6px;border-radius:6px;background:var(--active);color:var(--t2);font-size:12px;font-weight:600;display:flex;align-items:center;justify-content:center;font-variant-numeric:tabular-nums;flex-shrink:0}
.col__cards{flex:1;min-height:70px;overflow-y:auto;overflow-x:hidden;padding:0 8px 4px;display:flex;flex-direction:column;gap:var(--card-gap)}
.col__cards::-webkit-scrollbar{width:4px}
.col__cards::-webkit-scrollbar-track{background:transparent}
.col__cards::-webkit-scrollbar-thumb{background:var(--line-2);border-radius:4px}
.col__cards.drag-over{position:relative}
.col__cards.drag-over::after{content:'';position:absolute;inset:0;border-radius:8px;background:var(--brand-soft);opacity:.25;pointer-events:none}

.card{background:var(--surface);border:1px solid var(--line);border-radius:10px;padding:var(--card-pad);cursor:grab;user-select:none;box-shadow:var(--shadow);transition:border-color .12s;animation:riseIn .22s ease-out both;position:relative}
.card:hover{border-color:var(--line-2)}
.card:active{cursor:grabbing}
.card.dragging{opacity:.32;pointer-events:none}

.card__name{font-size:14.5px;font-weight:600;letter-spacing:-.012em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.card__msg{font-size:13px;color:var(--t2);margin-top:4px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;display:var(--msg)}
.card__footer{display:flex;flex-wrap:wrap;align-items:center;gap:6px;row-gap:7px;margin-top:10px;min-height:22px}
.card__tag{display:inline-flex;align-items:center;gap:5px;height:21px;padding:0 7px;border:1px solid var(--line-2);border-radius:6px;font-size:11.5px;font-weight:500;color:var(--t2);white-space:nowrap;flex-shrink:0}
.card__tag-dot{width:8px;height:8px;border-radius:2px;flex-shrink:0}
.card__meta{display:flex;align-items:center;gap:7px;margin-left:auto;flex-shrink:0}
.card__time{font-size:12px;color:var(--t3);font-variant-numeric:tabular-nums}
.card__av{width:22px;height:22px;border-radius:7px;display:flex;align-items:center;justify-content:center;font-size:9.5px;font-weight:700;letter-spacing:.01em;flex-shrink:0}
.card__unread{position:absolute;top:-5px;right:-5px;min-width:18px;height:18px;padding:0 5px;border-radius:9px;background:var(--brand);color:#fff;font-size:11px;font-weight:700;display:flex;align-items:center;justify-content:center;font-variant-numeric:tabular-nums}

.ctx{position:fixed;background:var(--surface);border:1px solid var(--line);border-radius:11px;box-shadow:var(--shadow-lg);padding:5px;z-index:2000;min-width:200px;animation:riseIn .12s ease-out}
.ctx__item{display:flex;align-items:center;gap:9px;padding:8px 9px;font-size:13.5px;width:100%;text-align:left;border-radius:7px;transition:background .08s;border:none;background:none;color:var(--t1);cursor:pointer;font-family:var(--ff)}
.ctx__item:hover{background:var(--hover)}
.ctx__item svg{flex-shrink:0;color:var(--t3)}
.ctx__item:hover svg{color:var(--t2)}
.ctx__arrow{margin-left:auto;color:var(--t4)}
.ctx__sep{height:1px;background:var(--line);margin:3px 6px}
.ctx__back{display:flex;align-items:center;gap:6px;padding:7px 9px;font-size:12.5px;font-weight:500;color:var(--t3);width:100%;text-align:left;border-radius:7px;margin-bottom:2px;transition:background .08s;border:none;background:none;cursor:pointer;font-family:var(--ff)}
.ctx__back:hover{background:var(--hover);color:var(--t2)}
.ctx__sub-title{padding:2px 9px 6px;font-size:11.5px;font-weight:600;color:var(--t3);text-transform:uppercase;letter-spacing:.04em}
.ctx__stage-dot{width:7px;height:7px;border-radius:2px;flex-shrink:0}

.toast{position:fixed;left:50%;bottom:26px;transform:translateX(-50%);z-index:3000;background:var(--t1);color:var(--bg);padding:10px 16px;border-radius:9px;font-size:13.5px;font-weight:500;box-shadow:var(--shadow-lg);white-space:nowrap;animation:riseIn .18s ease-out}

/* Loading skeleton */
@keyframes shimmer{0%{background-position:200% 0}to{background-position:-200% 0}}
.kb-loading{height:100%;display:flex;flex-direction:column;animation:fadeIn .2s ease-out}
@keyframes fadeIn{from{opacity:0}to{opacity:1}}
.kb-loading__header{height:60px;display:flex;align-items:center;gap:10px;padding:0 20px;border-bottom:1px solid var(--line)}
.kb-loading__tabs{height:46px;display:flex;align-items:center;gap:22px;padding:0 20px;border-bottom:1px solid var(--line)}
.kb-loading__board{display:flex;gap:10px;padding:12px 16px;flex:1;overflow:hidden}
.kb-loading__col{flex:1 0 240px;min-width:240px;background:var(--col);border-radius:12px;padding:14px 10px;display:flex;flex-direction:column;gap:10px}
.sk{border-radius:8px;background:linear-gradient(90deg,var(--line) 25%,var(--hover) 50%,var(--line) 75%);background-size:200% 100%;animation:shimmer 1.8s ease-in-out infinite}
.sk--title{width:90px;height:22px;border-radius:6px}
.sk--search{width:200px;height:36px;margin-left:auto}
.sk--btn{width:80px;height:36px}
.sk--tab{width:70px;height:22px;border-radius:6px}
.sk--col-head{width:60%;height:18px;border-radius:6px}
.sk--card{height:90px;border-radius:10px}

@media(max-width:768px){
  .header__row{flex-wrap:wrap;height:auto;padding:10px 16px;gap:8px}
  .header__search{min-width:140px;max-width:none;flex:1}
  .header__stat{display:none}
  .header__tabs{margin-left:0}
  .filters__row{flex-direction:column;align-items:stretch}
  .filters__field select{width:100%}
}
</style>

<style>
/* Dark mode — Chatwoot uses body.dark class */
body.dark .kb{
  --bg:#16181c;--surface:#202429;--col:#1b1e23;--hover:#23272d;--active:#2a2f36;--field:#1b1e23;
  --t1:#e7e9ec;--t2:#9ba1aa;--t3:#6e757f;--t4:#4a515a;
  --line:#2b3037;--line-2:#3a4048;
  --brand:#4d97ff;--brand-soft:#16283f;--brand-text:#7fb4ff;
  --ok:#4ade80;--warn:#fbbf24;--bad:#fb7185;
  --shadow:0 1px 2px rgba(0,0,0,.35);--shadow-lg:0 12px 28px rgba(0,0,0,.5),0 2px 6px rgba(0,0,0,.35);
}
/* Global (unscoped) styles for ghost/placeholder appended to document.body */
.card.drag-ghost{
  background:var(--surface,#fff);border:1px solid var(--brand,#1f6feb);border-radius:10px;
  padding:12px 13px;box-shadow:0 12px 28px rgba(16,24,40,.16),0 2px 6px rgba(16,24,40,.08);
  transform:rotate(1.4deg);z-index:1000;pointer-events:none;position:fixed;animation:none;
  font-family:'Inter',-apple-system,system-ui,sans-serif;font-size:14px;color:var(--t1,#1f2937);
  -webkit-font-smoothing:antialiased;line-height:1.45;
  overflow:hidden;box-sizing:border-box;
}
.card.drag-ghost .card__name{font-size:14.5px;font-weight:600;letter-spacing:-.012em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.card.drag-ghost .card__msg{font-size:13px;color:var(--t2,#6b7280);margin-top:4px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.card.drag-ghost .card__footer{display:flex;flex-wrap:wrap;align-items:center;gap:6px;row-gap:7px;margin-top:10px;min-height:22px}
.card.drag-ghost .card__tag{display:inline-flex;align-items:center;gap:5px;height:21px;padding:0 7px;border:1px solid var(--line-2,#dcdfe3);border-radius:6px;font-size:11.5px;font-weight:500;color:var(--t2,#6b7280);white-space:nowrap;flex-shrink:0}
.card.drag-ghost .card__tag-dot{width:8px;height:8px;border-radius:2px;flex-shrink:0}
.card.drag-ghost .card__meta{display:flex;align-items:center;gap:7px;margin-left:auto;flex-shrink:0}
.card.drag-ghost .card__time{font-size:12px;color:var(--t3,#9ca3af);font-variant-numeric:tabular-nums}
.card.drag-ghost .card__av{width:22px;height:22px;border-radius:7px;display:flex;align-items:center;justify-content:center;font-size:9.5px;font-weight:700;letter-spacing:.01em;flex-shrink:0}
.card.drag-ghost .card__unread{position:absolute;top:-5px;right:-5px;min-width:18px;height:18px;padding:0 5px;border-radius:9px;background:var(--brand,#1f6feb);color:#fff;font-size:11px;font-weight:700;display:flex;align-items:center;justify-content:center}
.drop-placeholder{border:none;border-radius:10px;background:var(--brand-soft,#eaf2fe);min-height:50px;flex-shrink:0;opacity:.5}
@keyframes riseIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
</style>
